/* NBA Paris Game 2027 — billetterie : quantité par catégorie, plan de salle, synchro avec le panier.
   Le panier est la référence : chaque catégorie affiche ce qu'il contient, la page n'envoie que la différence. */
(() => {
  const money = (cents) => {
    const v = cents / 100;
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: (window.nbaConfig && window.nbaConfig.currency) || 'EUR', minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(v);
  };
  const plural = (n) => `${n} billet${n > 1 ? 's' : ''}`;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const cartUrl = () => (window.routes ? window.routes.cart_url : '/cart');
  const postJSON = (url, body) =>
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) }).then(async (res) => {
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Opération impossible');
      return data;
    });

  const initTicket = (root) => {
    const orderMax = +root.dataset.orderMax || 4;
    const low = +root.dataset.low || 50;
    const cats = [...root.querySelectorAll('[data-nba-cat]')].map((el) => ({
      el,
      id: el.dataset.id,
      zone: el.dataset.zone,
      name: el.dataset.name,
      price: +el.dataset.price,
      stock: el.dataset.stock === '' ? Infinity : +el.dataset.stock,
      available: el.dataset.available === 'true',
      qty: 0,
      section: null,
      cartQty: 0,
      cartSection: null,
      cartLines: [],
      out: el.querySelector('output'),
      minus: el.querySelector('[data-step="-1"]'),
      plus: el.querySelector('[data-step="1"]'),
      badge: null,
    }));
    let otherItems = 0; // articles du panier qui ne sont pas des billets de cette page
    const map = root.querySelector('[data-nba-seatmap]');
    const zones = [...map.querySelectorAll('.nba-zone')];
    const tooltip = map.querySelector('[data-nba-tooltip]');
    const legend = [...root.querySelectorAll('[data-nba-legend]')];
    const lines = root.querySelector('[data-nba-lines]');
    const totalEl = root.querySelector('[data-nba-total]');
    const countEl = root.querySelector('[data-nba-count]');
    const submit = root.querySelector('[data-nba-submit]');
    const submitLabel = root.querySelector('[data-nba-submit-label]');
    const errorEl = root.querySelector('[data-nba-error]');
    const sticky = root.querySelector('[data-nba-sticky]');
    const stickyBtn = root.querySelector('[data-nba-sticky-btn]');
    const limitEl = root.querySelector('[data-nba-limit]');
    const limitCount = root.querySelector('[data-nba-limit-count]');
    const pips = limitEl ? [...limitEl.querySelectorAll('.nba-limit__pips i')] : [];
    const note = root.querySelector('[data-nba-limit-note]');
    const byZone = (zone) => cats.find((c) => c.zone === zone);
    const catColor = (c) => getComputedStyle(c.el).getPropertyValue('--cat');

    /* ---------- Sections ouvertes : petites séries aléatoires (graine fixe par anneau) ---------- */
    const labels = [...map.querySelectorAll('.nba-seatmap__labels text')];
    const seeded = (seed) => () => {
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const pick = (rand, arr) => arr[Math.floor(rand() * arr.length)];
    const openSet = new Set();
    const alwaysOpen = ['211', '313'];
    cats.forEach((c, ci) => {
      const rings = {};
      zones
        .filter((z) => z.dataset.zone === c.zone && /^\d+$/.test(z.dataset.section))
        .forEach((z) => (rings[z.dataset.section[0]] = rings[z.dataset.section[0]] || []).push(z));
      Object.values(rings).forEach((list, ri) => {
        list.sort((x, y) => +x.dataset.section - +y.dataset.section);
        const rand = seeded(2027 + ci * 97 + ri * 31);
        let i = Math.floor(rand() * 3);
        while (i < list.length) {
          const run = pick(rand, [1, 1, 2, 2, 3]);
          for (let k = 0; k < run && i < list.length; k++, i++) openSet.add(list[i]);
          i += pick(rand, [1, 2, 2, 3, 4, 5]);
        }
      });
    });
    zones.forEach((z) => { if (alwaysOpen.includes(z.dataset.section)) openSet.add(z); });
    const setOut = (z, out) => {
      z.classList.toggle('is-soldout', out);
      const t = labels.find((l) => l.textContent === z.dataset.section);
      if (t) t.classList.toggle('is-out', out);
    };
    zones.forEach((z) => {
      const c = byZone(z.dataset.zone);
      const randomOut = c && c.available && /^\d+$/.test(z.dataset.section) && !openSet.has(z);
      if (!c || !c.available || randomOut) setOut(z, true);
    });
    const openZones = (c) => zones.filter((z) => z.dataset.zone === c.zone && !z.classList.contains('is-soldout'));
    cats.forEach((c) => {
      if (!c.available) return;
      for (const z of zones.filter((x) => x.dataset.zone === c.zone)) {
        if (openZones(c).length >= 2) break;
        setOut(z, false);
      }
    });
    legend.forEach((b) => {
      const c = byZone(b.dataset.nbaLegend);
      if (!c || !c.available) b.classList.add('is-soldout');
    });

    /* Badge « Dans le panier » sur chaque catégorie */
    cats.forEach((c) => {
      const info = c.el.querySelector('.nba-cat__info');
      if (!info || !c.available) return;
      c.badge = document.createElement('span');
      c.badge.className = 'nba-cat__incart';
      c.badge.hidden = true;
      info.appendChild(c.badge);
    });

    /* ---------- Confirmation sur le plan ---------- */
    const toast = document.createElement('div');
    toast.className = 'nba-map-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    map.appendChild(toast);
    let toastTimer;
    const notify = (html, warn) => {
      toast.innerHTML = html;
      toast.classList.toggle('is-warn', !!warn);
      toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
    };

    /* ---------- État ---------- */
    const totalQty = () => cats.reduce((n, c) => n + c.qty, 0);
    const cartTickets = () => cats.reduce((n, c) => n + c.cartQty, 0);
    const used = () => totalQty() + otherItems;
    const isDirty = () => cats.some((c) => c.qty !== c.cartQty || (c.qty > 0 && c.section !== c.cartSection));

    // Le panier fait foi : on recale chaque catégorie sur son contenu
    const applyCart = (cart) => {
      let tickets = 0;
      cats.forEach((c) => {
        c.cartLines = cart.items.filter((i) => String(i.variant_id) === c.id);
        c.cartQty = c.cartLines.reduce((n, i) => n + i.quantity, 0);
        const sec = c.cartLines.length && c.cartLines[0].properties ? c.cartLines[0].properties.Section : null;
        c.cartSection = sec && /^\d+$/.test(sec) ? sec : null;
        c.qty = c.cartQty;
        c.section = c.cartSection;
        tickets += c.cartQty;
      });
      otherItems = Math.max(0, cart.item_count - tickets);
      render();
    };
    const refreshFromServer = () =>
      fetch(cartUrl() + '.js')
        .then((res) => res.json())
        .then(applyCart)
        .catch(() => {});

    /* ---------- Rendu ---------- */
    const render = () => {
      const count = totalQty();
      const total = cats.reduce((n, c) => n + c.qty * c.price, 0);
      const capped = used() >= orderMax;
      const dirty = isDirty();

      cats.forEach((c) => {
        if (!c.qty) c.section = null;
        if (!c.out) return;
        c.out.textContent = c.qty;
        c.minus.disabled = c.qty <= 0;
        c.plus.disabled = c.qty >= c.stock;
        c.plus.classList.toggle('is-capped', capped);
        c.plus.setAttribute('aria-disabled', capped ? 'true' : 'false');
        c.plus.title = capped ? `Limite de ${orderMax} billets par commande` : '';
        c.el.classList.toggle('is-picked', c.qty > 0);
        if (c.badge) {
          let text = '';
          if (c.cartQty > 0 && c.qty === c.cartQty) text = `Dans votre panier`;
          else if (c.cartQty > 0) text = `Panier : ${c.cartQty} → ${c.qty}`;
          c.badge.textContent = text;
          c.badge.hidden = !text;
          c.badge.classList.toggle('is-changed', c.cartQty > 0 && c.qty !== c.cartQty);
        }
      });

      map.classList.toggle('has-selection', count > 0);
      zones.forEach((z) => {
        const c = byZone(z.dataset.zone);
        const picked = !!c && c.qty > 0 && !z.classList.contains('is-soldout');
        z.classList.toggle('is-picked', picked);
        z.classList.toggle('is-chosen', picked && c.section === z.dataset.section);
        z.classList.toggle('is-dim', picked && !!c.section && c.section !== z.dataset.section);
      });
      legend.forEach((b) => {
        const c = byZone(b.dataset.nbaLegend);
        b.classList.toggle('is-picked', !!c && c.qty > 0);
      });

      const picked = cats.filter((c) => c.qty > 0);
      lines.innerHTML = picked.length
        ? picked
            .map((c) => {
              const where = c.section ? 'Section ' + c.section : 'Section attribuée au paiement';
              const state = c.cartQty > 0 && c.qty === c.cartQty && c.section === c.cartSection ? ' · dans le panier' : '';
              return `<li><span><i class="nba-swatch" style="--cat:${catColor(c)}"></i>${c.qty} × ${c.name}<em>${where}${state}</em></span><b>${money(c.qty * c.price)}</b></li>`;
            })
            .join('')
        : '<li class="nba-summary__empty">Aucune place sélectionnée</li>';
      totalEl.textContent = money(total);
      countEl.textContent = plural(count);

      // Jauge de la limite
      const u = used();
      if (limitCount) limitCount.textContent = u;
      pips.forEach((pip, i) => {
        pip.classList.remove('is-cart');
        pip.classList.toggle('is-on', i < u);
      });
      if (limitEl) limitEl.classList.toggle('is-full', u >= orderMax);
      if (note) {
        const msg = u >= orderMax ? `<b>Vous avez atteint la limite de ${orderMax} billets par commande.</b> Pour changer de catégorie, retirez d'abord un billet.` : '';
        note.innerHTML = msg;
        note.hidden = !msg;
        note.classList.toggle('is-full', !!msg);
      }

      // Bouton principal : ajouter, mettre à jour, ou voir le panier
      let label = 'Sélectionnez vos places';
      let mode = 'none';
      if (dirty && cartTickets() === 0) { label = `Ajouter au panier · ${money(total)}`; mode = 'save'; }
      else if (dirty && count === 0) { label = 'Retirer du panier'; mode = 'save'; }
      else if (dirty) { label = `Mettre à jour le panier · ${money(total)}`; mode = 'save'; }
      else if (cartTickets() > 0) { label = `Voir mon panier · ${money(total)}`; mode = 'view'; }
      submit.dataset.mode = mode;
      submit.disabled = mode === 'none';
      submit.classList.toggle('is-view', mode === 'view');
      submitLabel.textContent = label;
      if (sticky) {
        sticky.querySelector('[data-nba-sticky-count]').textContent = count ? plural(count) : 'Aucune place';
        sticky.querySelector('[data-nba-sticky-total]').textContent = count ? money(total) : 'Choisissez une catégorie';
        stickyBtn.textContent = mode === 'save' ? (cartTickets() ? 'Mettre à jour' : 'Ajouter au panier') : mode === 'view' ? 'Voir mon panier' : 'Choisir mes places';
      }
    };

    const warnLimit = () => {
      [limitEl, note].forEach((el) => {
        if (!el) return;
        el.classList.remove('is-alert');
        void el.offsetWidth;
        el.classList.add('is-alert');
      });
      if (note && !note.hidden && window.matchMedia('(max-width: 989px)').matches) {
        note.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
      }
    };

    const change = (c, delta) => {
      if (!c || !c.available) return false;
      if (delta > 0 && used() >= orderMax) {
        warnLimit();
        return false;
      }
      const next = c.qty + delta;
      if (next < 0 || next > c.stock) return false;
      c.qty = next;
      errorEl.hidden = true;
      render();
      return true;
    };

    const flash = (c, scroll) => {
      c.el.classList.remove('is-flash');
      void c.el.offsetWidth;
      c.el.classList.add('is-flash');
      if (scroll && window.matchMedia('(max-width: 989px)').matches) {
        c.el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
    };

    /* ---------- Plan ---------- */
    const highlight = (zone) => zones.forEach((z) => z.classList.toggle('is-hover', !!zone && z.dataset.zone === zone));
    const showTip = (zoneEl, e) => {
      const c = byZone(zoneEl.dataset.zone);
      highlight(zoneEl.dataset.zone);
      if (!tooltip || !c) return;
      const section = zoneEl.dataset.section;
      const out = zoneEl.classList.contains('is-soldout');
      let status = 'Disponible';
      if (out) status = c.available ? 'Section complète' : 'Catégorie complète';
      else if (c.stock !== Infinity && c.stock <= low) status = `Plus que ${c.stock} places`;
      tooltip.innerHTML = `
        <div class="nba-map-tooltip__cat"><span class="nba-swatch" style="--cat:${!out ? catColor(c) : '#9a9a9a'}"></span>${c.name}</div>
        <div class="nba-map-tooltip__row"><span>${/^\d+$/.test(section) ? 'Section ' + section : section}</span><b>${!out ? money(c.price) : 'Complet'}</b></div>
        <div class="nba-map-tooltip__row"><span>${status}</span><span>${!out ? 'Cliquez pour choisir' : ''}</span></div>`;
      const box = map.getBoundingClientRect();
      const x = e && e.clientX ? e.clientX - box.left : box.width / 2;
      const y = e && e.clientY ? e.clientY - box.top : box.height / 2;
      tooltip.style.left = `${Math.min(Math.max(x, 105), box.width - 105)}px`;
      tooltip.style.top = `${Math.max(y, 92)}px`;
      tooltip.classList.add('is-visible');
    };
    const hideTip = () => {
      highlight(null);
      if (tooltip) tooltip.classList.remove('is-visible');
    };

    cats.forEach((c) => {
      if (c.minus) c.minus.addEventListener('click', () => change(c, -1));
      if (c.plus) c.plus.addEventListener('click', () => change(c, 1));
      c.el.addEventListener('pointerenter', () => highlight(c.zone));
      c.el.addEventListener('pointerleave', () => highlight(null));
    });
    zones.forEach((z) => {
      z.addEventListener('pointermove', (e) => showTip(z, e));
      z.addEventListener('pointerleave', hideTip);
      z.addEventListener('click', (e) => {
        const c = byZone(z.dataset.zone);
        if (!c || !c.available || z.classList.contains('is-soldout')) {
          notify(`<b>Section ${z.dataset.section}</b> complète · choisissez une section en couleur`, true);
          return showTip(z, e);
        }
        const wasEmpty = c.qty === 0;
        if (wasEmpty && !change(c, 1)) {
          notify(`Limite de ${orderMax} billets par commande atteinte`, true);
          return;
        }
        c.section = z.dataset.section;
        render();
        notify(wasEmpty
          ? `<b>Section ${c.section}</b> ajoutée · ${c.name} · ${money(c.price)}`
          : `<b>Section ${c.section}</b> sélectionnée pour vos ${plural(c.qty)} ${c.name}`);
        flash(c, false);
        if (e.pointerType !== 'mouse') setTimeout(hideTip, 1500);
      });
    });
    legend.forEach((b) => {
      b.addEventListener('pointerenter', () => highlight(b.dataset.nbaLegend));
      b.addEventListener('pointerleave', () => highlight(null));
      b.addEventListener('click', () => {
        const c = byZone(b.dataset.nbaLegend);
        if (c) flash(c, true);
      });
    });

    /* ---------- Envoi au panier : seulement la différence ---------- */
    const openDrawer = () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = cartUrl();
    };
    const save = async () => {
      if (submit.dataset.mode === 'view') return openDrawer();
      if (submit.dataset.mode !== 'save') return;
      submit.classList.add('loading');
      submit.disabled = true;
      errorEl.hidden = true;
      try {
        let added = false;
        for (const c of cats) {
          const sectionProp = { Section: c.section ? c.section : 'Attribuée au paiement' };
          if (c.qty < c.cartQty) {
            // retirer des billets, en partant des dernières lignes
            let remove = c.cartQty - c.qty;
            for (const line of [...c.cartLines].reverse()) {
              if (!remove) break;
              const next = Math.max(0, line.quantity - remove);
              remove -= line.quantity - next;
              await postJSON('/cart/change.js', { id: line.key, quantity: next });
            }
          } else if (c.qty > c.cartQty) {
            await postJSON('/cart/add.js', { items: [{ id: +c.id, quantity: c.qty - c.cartQty, properties: sectionProp }] });
            added = true;
          }
          if (c.qty > 0 && c.qty === c.cartQty && c.section !== c.cartSection && c.cartLines.length === 1) {
            // même quantité, nouvelle section choisie sur le plan
            await postJSON('/cart/change.js', { id: c.cartLines[0].key, quantity: c.qty, properties: sectionProp });
          }
        }
        if (added) document.dispatchEvent(new CustomEvent('nba:cart-added'));
        const sections = await fetch(`${window.location.pathname}?sections=${root.dataset.sections}`).then((r) => r.json());
        await refreshFromServer();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nba-ticket', cartData: { item_count: cartTickets() + otherItems } });
        }
        const drawer = document.querySelector('cart-drawer');
        if (drawer && sections) {
          drawer.classList.toggle('is-empty', cartTickets() + otherItems === 0);
          drawer.renderContents({ sections });
        } else {
          window.location.href = cartUrl();
        }
      } catch (err) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
        refreshFromServer();
      } finally {
        submit.classList.remove('loading');
        render();
      }
    };
    submit.addEventListener('click', save);

    /* ---------- Synchro : tiroir, page panier, retour arrière, autre onglet ---------- */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => { if (!event || event.source !== 'nba-ticket') refreshFromServer(); });
    }
    window.addEventListener('pageshow', (e) => { if (e.persisted) refreshFromServer(); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshFromServer(); });

    /* ---------- Barre mobile ---------- */
    if (sticky) {
      stickyBtn.addEventListener('click', () => {
        if (submit.dataset.mode === 'none') root.querySelector('.nba-cats').scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        else save();
      });
      if ('IntersectionObserver' in window) {
        let submitVisible = false;
        let bodyVisible = true;
        const refresh = () => sticky.classList.toggle('is-visible', !submitVisible && bodyVisible);
        new IntersectionObserver(([e]) => { submitVisible = e.isIntersecting; refresh(); }).observe(submit);
        new IntersectionObserver(([e]) => { bodyVisible = e.isIntersecting; refresh(); }).observe(root.querySelector('.nba-pdp__body'));
      }
    }

    /* ---------- Démarrage : état du panier rendu par Liquid, puis ?variant= ---------- */
    const initial = root.querySelector('[data-nba-cart]');
    try {
      if (initial) applyCart(JSON.parse(initial.textContent));
    } catch (e) {
      refreshFromServer();
    }
    const preset = new URLSearchParams(window.location.search).get('variant');
    const presetCat = cats.find((c) => c.id === preset);
    if (presetCat && presetCat.available && presetCat.qty === 0) {
      change(presetCat, 1);
      flash(presetCat, false);
    }
    render();
  };

  const start = () => document.querySelectorAll('[data-nba-ticket]').forEach(initTicket);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
